import React, { createContext, useContext, useCallback, useMemo, useRef } from 'react';
import { ChatContextType, ChatSessionContextType, ChatStreamingContextType, MessageContext } from '../types/domain';
import { useChatTanStack } from '../hooks/data/useChatTanStack';
import { useAppConfig } from '../../app-config';

const ChatSessionContext = createContext<ChatSessionContextType | undefined>(undefined);
const ChatStreamingContext = createContext<ChatStreamingContextType | undefined>(undefined);

/** Reads both session and streaming context (re-renders on every token). Use useChatSession() when streaming state is not needed. */
export const useChatContext = (): ChatContextType => {
  const session = useContext(ChatSessionContext);
  const streaming = useContext(ChatStreamingContext);
  if (!session || !streaming) {
    throw new Error('useChatContext must be used within a ChatProvider');
  }
  return { ...session, ...streaming };
};

/** Reads only session-level state. Does NOT re-render on streaming token updates. */
export const useChatSession = (): ChatSessionContextType => {
  const context = useContext(ChatSessionContext);
  if (!context) {
    throw new Error('useChatSession must be used within a ChatProvider');
  }
  return context;
};

/** Reads only high-frequency streaming state (isTyping, streamingContent, etc.). */
export const useChatStreaming = (): ChatStreamingContextType => {
  const context = useContext(ChatStreamingContext);
  if (!context) {
    throw new Error('useChatStreaming must be used within a ChatProvider');
  }
  return context;
};

interface ChatProviderProps {
  children: React.ReactNode;
}

export const ChatProvider: React.FC<ChatProviderProps> = ({ children }) => {
  const chat = useChatTanStack();
  const { availableFeatures } = useAppConfig();

  const [externalLoadingOperations, setExternalLoadingOperations] = React.useState<Set<string>>(new Set());
  const isExternalLoading = externalLoadingOperations.size > 0;
  // Derived from session data: a session is historical when the backend marks it inactive.
  const isHistoricalSession = (chat.currentSession?.isActive ?? null) === false;

  // Use a ref so stopGeneration never changes reference — avoids cascade re-renders
  const abortRef = useRef<() => void>(() => {});
  abortRef.current = () => { chat.streamChatHook.abort(); };
  const stopGeneration = useCallback(() => abortRef.current(), []);

  const mutateAsyncRef = useRef(chat.streamChatHook.mutateAsync);
  mutateAsyncRef.current = chat.streamChatHook.mutateAsync;

  // Calculate if any chat operation should disable user actions
  const isChatActionDisabled = chat.streamChatHook.isPending ||
    chat.createSessionMutation.isPending ||
    chat.switchSessionMutation.isPending ||
    chat.deleteSessionMutation.isPending ||
    chat.updateTitleMutation.isPending ||
    chat.addSystemMessageMutation.isPending ||
    chat.sendWidgetResponseMutation.isPending ||
    isExternalLoading;

  // Stable callbacks for external loading management
  const registerExternalLoading = useCallback((operationId: string) => {
    setExternalLoadingOperations(prev => new Set([...prev, operationId]));
  }, []);

  const unregisterExternalLoading = useCallback((operationId: string) => {
    setExternalLoadingOperations(prev => {
      const newSet = new Set(prev);
      newSet.delete(operationId);
      return newSet;
    });
  }, []);

  const value: ChatSessionContextType = useMemo(() => ({
    currentSession: chat.currentSession ?? null,
    sessions: chat.sessions,
    isLoading: chat.isLoading,
    isChatActionDisabled,
    isSwitchingSession: chat.switchSessionMutation.isPending,
    isCreatingSession: chat.createSessionMutation.isPending,
    hasSessionHistory: chat.sessions.length > 0,
    isHistoricalSession,

    registerExternalLoading,
    unregisterExternalLoading,
    stopGeneration,

    sendMessage: async (content: string, context?: MessageContext) => {
      const enhancedContext: MessageContext = {
        ...context,
        availableFeatures: availableFeatures,
      };

      await mutateAsyncRef.current({ content, context: enhancedContext });
    },
    setInsightsContext: chat.setInsightsContext,
    setSummaryThreadId: chat.setSummaryThreadId,
    createNewSession: async () => {
      await chat.createSessionMutation.mutateAsync();
    },
    switchToSession: async (sessionId: string) => {
      await chat.switchSessionMutation.mutateAsync(sessionId);
    },
    deleteSession: async (sessionId: string) => {
      await chat.deleteSessionMutation.mutateAsync(sessionId);
    },
    updateSessionTitle: async (sessionId: string, title: string) => {
      await chat.updateTitleMutation.mutateAsync({ sessionId, title });
    },
    addSystemMessage: async (content: string, threadId?: string, handlerType?: string) => {
      return await chat.addSystemMessageMutation.mutateAsync({ content, threadId, handlerType });
    },
    sendWidgetResponse: async (widgetId: string, selectedOptions: string[], buttonAction?: string, feedbackText?: string) => {
      await chat.sendWidgetResponseMutation.mutateAsync({ widgetId, selectedOptions, buttonAction, feedbackText });
    },
    submitFeedback: async (
      threadId: string,
      rating: 'positive' | 'negative',
      options?: { comment?: string; messageId?: string; agentId?: string },
    ) => {
      await chat.submitFeedbackMutation.mutateAsync({ threadId, rating, ...options });
    },
    fetchSessions: () => chat.fetchSessions(),
  }), [
    chat.currentSession,
    chat.sessions,
    chat.isLoading,
    chat.createSessionMutation,
    chat.switchSessionMutation,
    chat.deleteSessionMutation,
    chat.updateTitleMutation,
    chat.addSystemMessageMutation,
    chat.sendWidgetResponseMutation,
    chat.setInsightsContext,
    chat.setSummaryThreadId,
    chat.submitFeedbackMutation,
    chat.fetchSessions,
    isChatActionDisabled,
    isHistoricalSession,
    availableFeatures,
    registerExternalLoading,
    unregisterExternalLoading,
    stopGeneration,
  ]);

  // Streaming state is read directly every render so it's always live,
  // without causing cascade re-renders from useMemo.
  const streamingValue: ChatStreamingContextType = useMemo(() => ({
    isTyping: chat.streamChatHook.isPending,
    isRouting: chat.streamChatHook.isRouting,
    wasRouting: chat.streamChatHook.wasRouting,
    streamingContent: chat.streamChatHook.streamingContent,
    liveReasoning: chat.streamChatHook.liveReasoning,
  }), [
    chat.streamChatHook.isPending,
    chat.streamChatHook.isRouting,
    chat.streamChatHook.wasRouting,
    chat.streamChatHook.streamingContent,
    chat.streamChatHook.liveReasoning,
  ]);

  return (
    <ChatSessionContext.Provider value={value}>
      <ChatStreamingContext.Provider value={streamingValue}>
        {children}
      </ChatStreamingContext.Provider>
    </ChatSessionContext.Provider>
  );
};
